import { DownOutlined, RetweetOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  defaultPaymentFrequency,
  getIrelandPaymentModels,
  paymentModels,
  pensionDeductionMethods,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useGetGroupingPayrollByPeriod } from '@nxsys/hooks/api/dashboard';
import {
  useGetPayroll,
  useSendEmailPayslipMultiple,
  useSendPayslip,
  useViewPayslipById,
} from '@nxsys/hooks/api/payroll';
import { usePerformReversalPayrolls } from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetPayrollListParams } from '@nxsys/store/slices/report/types';
import { Payroll } from '@nxsys/store/slices/revenueFilings/types';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';
import { GetGroupingPayrollByPeriodResponse } from '@nxsys/types/api/dashboard';
import { Icons } from '@nxsys/types/icons';
import { getAppConfig, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import {
  getShownEmployeePension,
  getShownEmployersPension,
  getShownPensionValue,
} from '@nxsys/utils/reported-pension';
import { Dropdown, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find, isEmpty, some } from 'lodash';
import TimesheetReverseModal from '../../timesheets/components/TimesheetReverseModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const EmailPayslipModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const canReverse = useCanReversePayroll();
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // Country-only: this modal is umbrella-only (Communication.tsx renders it
  // only when `!isBureau`), so the old `isBureau &&` gate meant an Ireland
  // tenant here never matched either branch and always fell through to `[]`.
  const isIreland = payrollCountry === IRELAND;
  const defaultPaymentModel = getAppConfig('paymentModels');
  const { payrollList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = payrollList;
  const validPaymentModels = useMemo(() => {
    return isUK
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : isIreland
        ? getIrelandPaymentModels(isBureau)
        : [];
  }, [isUK, isIreland, isBureau]);

  const [isGrouping, setIsGrouping] = useState(true);
  const [selectedRows, setSelectedRows] = useState<Payroll[]>([]);
  const [reverseModal, setReverseModal] = useState<{
    isOpen: boolean;
    payrollIds: string[];
  }>({
    isOpen: false,
    payrollIds: [],
  });
  const toggleReverseModal = () => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const {
    value: payrollGrouping,
    loading: isPayrollGroupingLoading,
    run: getPayrollGrouping,
  } = useGetGroupingPayrollByPeriod();
  const payrollGroupingData = useMemo(
    () => payrollGrouping?.data,
    [payrollGrouping],
  );

  useEffect(() => {
    if (isOpen) {
      getPayrollGrouping();
    }
  }, [isOpen]);

  const handleFetchData = (params: GetPayrollListParams) => {
    const newParams = {
      ...params,
      appName: 'BUREAU',
      paymentModels: validPaymentModels,
    };
    dispatch(reportActions.getPayrollList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleView = useCallback(
    (id: string) => {
      navigate(`/reports/payroll/${id}`, {
        query: {
          from: '/dashboards',
        },
      });
    },
    [navigate],
  );

  const { run: viewPayslipById } = useViewPayslipById();
  const handleViewPayslipById = (id: string) => {
    viewPayslipById(id).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      }
    });
  };

  const { run: performReversalPayrolls } = usePerformReversalPayrolls();
  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls(params).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };
  const handleOpenReverse = (data: Payroll[]) => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: true,
      payrollIds: data.map((item) => item.id),
    }));
  };
  const handleOpenReverseSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleOpenReverse(selectedRows);
  };

  const { run: sendPayslip } = useSendPayslip();
  const handleSendPayslip = (data: Payroll) => {
    const params = {
      id: data.selfBillRef?.id || '',
      companyId: data.companyRef?.id || '',
      subcontractorId: data.subcontractorRef?.id || '',
    };
    sendPayslip(params).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentPayslipSuccess'],
        });
        refetchData();
      }
    });
  };

  const columnsUK: ColumnsType<Payroll> = [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',
      render: (record) => {
        const { selfBillRef, isSendingEmailSelfBill } = record;
        const latestEmailLog = !isEmpty(selfBillRef?.emailLogs)
          ? selfBillRef.emailLogs?.reduce((latest: any, log: any) => {
              return new Date(log.createdAt) > new Date(latest.createdAt)
                ? log
                : latest;
            }, selfBillRef.emailLogs[0])
          : null;
        const isSentEmailSelfBillSuccess =
          !selfBillRef?.isSendEmailFailed &&
          !selfBillRef?.errorSendEmailFailed &&
          (selfBillRef?.isSendEmailSelfBill ||
            latestEmailLog?.status === 'SUCCESS');
        const isSentEmailSelfBillFailed =
          selfBillRef?.isSendEmailFailed && selfBillRef?.errorSendEmailFailed;

        return (
          <ul className="flex items-center justify-center gap-2">
            <li>
              <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
                <Tooltip title={messages['common.view']}>
                  <span className="cursor-pointer" onClick={() => handleView(record.id)}>
                    <Icons.IconEye />
                  </span>
                </Tooltip>
                {selfBillRef && (
                  <Tooltip title={messages['common.download']}>
                    <span className="cursor-pointer" onClick={() => handleViewPayslipById(record.selfBillRef?.id)}>
                      <Icons.IconDownload />
                    </span>
                  </Tooltip>
                )}
                {canReverse && (
                  <Tooltip title={messages['timesheet.reverse']}>
                    <span className="cursor-pointer" onClick={() => handleOpenReverse([record])}>
                      <RetweetOutlined style={{ color: 'orange' }} />
                    </span>
                  </Tooltip>
                )}
              </div>
            </li>
            {selfBillRef && (
              <>
                <li>
                  <AppIconButton
                    title={
                      isSendingEmailSelfBill
                        ? messages['common.sending']
                        : isSentEmailSelfBillSuccess
                          ? messages['common.emailSentPayslipSuccess']
                          : isSentEmailSelfBillFailed
                            ? selfBillRef?.errorSendEmailFailed || messages['common.emailSentPayslipFailed']
                            : messages['common.sendEmailPayslip']
                    }
                    icon={
                      isSentEmailSelfBillFailed ? (
                        <Icons.IconSendEmailFailed />
                      ) : (
                        <Icons.IconEmail
                          color={
                            isSendingEmailSelfBill
                              ? '#ff9900'
                              : isSentEmailSelfBillSuccess
                                ? defaultTheme.theme.palette.green[500]
                                : '#666666'
                          }
                        />
                      )
                    }
                    onClick={() => handleSendPayslip(record)}
                  />
                </li>
              </>
            )}
          </ul>
        );
      },
      width: 100,
      fixed: 'left',
    },
    {
      key: 'paymentCompany',
      title: 'Company ID',
      dataIndex: 'subcontractorRef',
      render: (value) => <span>{value?.companyPayment?.id || '_'}</span>,
    },
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 140,
      render: (value) => <div className="row_center">{value || '_'}</div>,
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => <div className="row_center">{value?.id || '_'}</div>,
    },
    {
      key: 'Forenames',
      title: 'Forenames',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.forename || ''}</div>
      ),
    },
    {
      key: 'Surname',
      title: 'Surname',
      dataIndex: 'subcontractorRef',
      sorter: true,
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.surname || ''}</div>
      ),
    },
    {
      key: 'jobDescription',
      title: 'Job Title',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.jobDescription || ''}</div>
      ),
    },
    {
      key: 'companyName',
      title: 'Employer',
      dataIndex: 'companyRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.companyName || '_'}</div>
      ),
    },
    {
      key: 'department',
      title: 'Department',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.department || '_'}</div>
      ),
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      sorter: true,
      width: 150,
      render: (_, record) => (
        <div className="row_center">{`${record?.taxPeriod || ''}`}</div>
      ),
    },
    {
      key: 'taxYear',
      title: 'Tax Year',
      align: 'right',
      sorter: true,
      width: 150,
      render: (_, record) => (
        <div className="row_center">{`${
          record?.taxYear || record?.selfBillRef?.taxYear || ''
        }`}</div>
      ),
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      align: 'center',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {value
            ? find(defaultPaymentFrequency, (item) => item?.value === value)
                ?.label
            : '_'}
        </div>
      ),
    },
    {
      key: 'timesheetValue',
      title: 'Gross Pay',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'hours',
      title: 'Units',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.totalHours || 0}</div>
      ),
    },
    {
      key: 'Salary',
      title: 'Salary',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.salary, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'holidayPay',
      title: 'Advanced Holiday Pay',
      dataIndex: 'rolledUpHolidayPay',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'holidayPayRetained',
      title: 'Holiday Pay Retained',
      dataIndex: 'retainedHolidayPay',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'holidayPaid',
      title: 'Holiday Paid',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            (record.holidayPaid || 0) - (record.rolledUpHolidayPay || 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Taxable',
      title: 'Taxable Pay',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value, record) => (
        <div className="row_center">
          {formatMoney(
            record?.displayedTaxablePay || value?.taxablePay,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'grossForTax',
      title: 'Gross for Tax',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.grossForTax,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'grossForNI',
      title: 'Gross for NI',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.grossForNI,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'otherPayment',
      title: 'Other Payment',
      dataIndex: '_otherPayment',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'otherDeduction',
      title: 'Other Deduction',
      dataIndex: '_otherDeduction',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Tax_Deducted',
      title: 'Tax Deducted',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.tax, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'NI_Empe',
      title: 'NI Empe',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            Math.max(value?.employeeNI || 0, 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'employersNI',
      title: 'NI Empr',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.employersNI,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Pension_Empe',
      title: 'Pension Empe',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            getShownEmployeePension(record),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Pension_Empe_Method',
      title: 'Empe Deduction Method',
      dataIndex: 'payeeUmbrellaValue',
      align: 'center',
      width: 250,
      render: (value) => {
        const method = value?.eePensionDeductionMethod;
        return (
          <div className="row_center">
            {method
              ? pensionDeductionMethods.find((item) => item.value === method)
                  ?.label
              : 'Percentage (%)'}
          </div>
        );
      },
    },
    {
      key: 'Pension_Empr',
      title: 'Pension Empr',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            getShownEmployersPension(record),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Pension_Empr_Method',
      title: 'Empr Deduction Method',
      dataIndex: 'payeeUmbrellaValue',
      align: 'center',
      width: 250,
      render: (value) => {
        const method = value?.erPensionDeductionMethod;
        return (
          <div className="row_center">
            {method
              ? pensionDeductionMethods.find((item) => item.value === method)
                  ?.label
              : 'Percentage (%)'}
          </div>
        );
      },
    },
    {
      key: 'AVC_Employer',
      title: 'AVC Employer',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.avcERPension,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'AVC_Employee',
      title: 'AVC Employee',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      // Under salary sacrifice the employee AVC is reported inside the
      // employer pension, so it must not be shown again here.
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            getShownPensionValue(record, 'avcEEPension'),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'studentLoan',
      title: 'Student Loan',
      dataIndex: 'studentLoan',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'postgraduateLoan',
      title: 'PG Student Loan',
      dataIndex: 'postgraduateLoan',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Attachment1',
      title: 'Attachment',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.attachmentOrderDeducted,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Sick Pay (SSP)',
      title: 'Sick Pay (SSP)',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.ssp, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Maternity Pay (SMP)',
      title: 'Maternity Pay (SMP)',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.smp, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Adoption Pay (SAP)',
      title: 'Adoption Pay (SAP)',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.sap, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Paternity Pay (SPP)',
      title: 'Paternity Pay (SPP)',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.spp || value?.sppa,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Additional SPP',
      title: 'Additional SPP',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: () => <div className="row_center">{'_'}</div>,
    },
    {
      key: 'grossForTaxYTD',
      title: 'Gross for Tax (YTD)',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            (record?.payeeUmbrellaValue?.grossForTaxTD || 0) +
              (record?.payeeUmbrellaValue?.p45GrossToDate || 0) +
              (record?.payeeUmbrellaValue?.p11?.grossForTax || 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'taxYTD',
      title: 'Tax (YTD)',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            (record?.payeeUmbrellaValue?.taxTD || 0) +
              (record?.payeeUmbrellaValue?.p45TaxToDate || 0) +
              (record?.payeeUmbrellaValue?.p11?.tax || 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Net_Pay',
      title: 'Net Pay',
      dataIndex: 'net',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Payment Date',
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </div>
      ),
    },
    {
      key: 'Payslip ID',
      title: 'Payslip ID',
      dataIndex: 'selfBillRef',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {value?.id?.replace('SFB', 'PAY') || '_'}
        </div>
      ),
    },
    {
      key: 'FPS Amount',
      title: 'FPS Amount',
      dataIndex: 'fpsAmount',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Tronc payment',
      title: 'Tronc payment',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.troncValue,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
  ];

  const columnsIreland: ColumnsType<Payroll> = [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',
      render: (record) => {
        const { selfBillRef, isSendingEmailSelfBill } = record;
        const latestEmailLog = !isEmpty(selfBillRef?.emailLogs)
          ? selfBillRef.emailLogs?.reduce((latest: any, log: any) => {
              return new Date(log.createdAt) > new Date(latest.createdAt)
                ? log
                : latest;
            }, selfBillRef.emailLogs[0])
          : null;
        const isSentEmailSelfBillSuccess =
          !selfBillRef?.isSendEmailFailed &&
          !selfBillRef?.errorSendEmailFailed &&
          (selfBillRef?.isSendEmailSelfBill ||
            latestEmailLog?.status === 'SUCCESS');
        const isSentEmailSelfBillFailed =
          selfBillRef?.isSendEmailFailed && selfBillRef?.errorSendEmailFailed;

        return (
          <ul className="flex items-center justify-center gap-2">
            <li>
              <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
                <Tooltip title={messages['common.view']}>
                  <span className="cursor-pointer" onClick={() => handleView(record.id)}>
                    <Icons.IconEye />
                  </span>
                </Tooltip>
                {selfBillRef && (
                  <Tooltip title={messages['common.download']}>
                    <span className="cursor-pointer" onClick={() => handleViewPayslipById(record.selfBillRef?.id)}>
                      <Icons.IconDownload />
                    </span>
                  </Tooltip>
                )}
                {canReverse && (
                  <Tooltip title={messages['timesheet.reverse']}>
                    <span className="cursor-pointer" onClick={() => handleOpenReverse([record])}>
                      <RetweetOutlined style={{ color: 'orange' }} />
                    </span>
                  </Tooltip>
                )}
              </div>
            </li>
            {selfBillRef && (
              <>
                <li>
                  <AppIconButton
                    title={
                      isSendingEmailSelfBill
                        ? messages['common.sending']
                        : isSentEmailSelfBillSuccess
                          ? messages['common.emailSentPayslipSuccess']
                          : isSentEmailSelfBillFailed
                            ? selfBillRef?.errorSendEmailFailed || messages['common.emailSentPayslipFailed']
                            : messages['common.sendEmailPayslip']
                    }
                    icon={
                      isSentEmailSelfBillFailed ? (
                        <Icons.IconSendEmailFailed />
                      ) : (
                        <Icons.IconEmail
                          color={
                            isSendingEmailSelfBill
                              ? '#ff9900'
                              : isSentEmailSelfBillSuccess
                                ? defaultTheme.theme.palette.green[500]
                                : '#666666'
                          }
                        />
                      )
                    }
                    onClick={() => handleSendPayslip(record)}
                  />
                </li>
              </>
            )}
          </ul>
        );
      },
      width: 100,
      fixed: 'left',
    },
    {
      key: 'paymentCompany',
      title: 'Company ID',
      dataIndex: 'subcontractorRef',
      render: (value) => <span>{value?.companyPayment?.id || '_'}</span>,
    },
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 140,
      render: (value) => <div className="row_center">{value || '_'}</div>,
    },
    {
      key: 'EmployeeID',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.agencyRef || '_'}</div>
      ),
    },
    {
      key: 'Forename',
      title: 'Forename',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.forename || '_'}</div>
      ),
    },
    {
      key: 'Surname',
      title: 'Surname',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.surname || '_'}</div>
      ),
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      sorter: true,
      width: 250,
      render: (_, record) => (
        <div className="row_center">{`${record?.taxPeriod || ''}/${
          record?.taxYear || record?.selfBillRef?.taxYear || ''
        }`}</div>
      ),
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      align: 'center',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {defaultPaymentFrequency?.find((e) => e.value === value)?.label ||
            '_'}
        </div>
      ),
    },
    {
      key: 'companyName',
      title: 'Employer',
      dataIndex: 'companyRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.companyName || '_'}</div>
      ),
    },
    {
      key: 'gross',
      title: 'Gross Pay',
      dataIndex: 'gross',
      align: 'right',
      sorter: true,
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'hours',
      title: 'Units',
      dataIndex: 'timeSheets',
      align: 'right',
      width: 200,
      render: (value) => {
        const totalHours = value?.reduce(
          (acc1: number, curr1: any) =>
            acc1 +
            curr1?.rates?.reduce(
              (acc2: number, curr2: any) => acc2 + curr2.units,
              0,
            ),
          0,
        );
        return <div className="row_center">{totalHours || 0}</div>;
      },
    },
    {
      key: 'Salary',
      title: 'Salary',
      dataIndex: 'salary',
      align: 'right',
      width: 250,
      render: (value, record) => (
        <div className="row_center">
          {formatMoney(
            payrollCountry !== IRELAND
              ? value || 0
              : record?.irelandValue?.salary || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'Notional Pay',
      title: 'Notional Pay',
      dataIndex: 'salary',
      align: 'right',
      width: 250,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            record?.irelandValue?.notionalPay || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'grossForTax',
      title: 'Taxable Gross',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.taxGross || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'taxDeducted',
      title: 'Tax Deducted',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.tax || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'uscGross',
      title: 'USCable Gross',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.uscGross || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'usc',
      title: 'USC',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.usc || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'eePrsiGross',
      title: 'Employee PRSIable Gross',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.eePrsiGross || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'eePRSI',
      title: 'Employee PRSI',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.eePRSI || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'erPrsiGross',
      title: 'Employer PRSIable Gross',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.erPrsiGross || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'erPRSI',
      title: 'Employer PRSI',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.erPRSI || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'pensionGross',
      title: 'Pensionable Gross',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.pensionGross || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'eePension',
      title: 'Employee Pension',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.eePension || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'erPension',
      title: 'Employer Pension',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.erPension || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'avcEEPension',
      title: 'AVC Employee',
      dataIndex: 'avcEEPension',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'avcERPension',
      title: 'AVC Employer',
      dataIndex: 'avcERPension',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'lptDeduction',
      title: 'LPT',
      dataIndex: 'irelandValue',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            value?.lptDeducted || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'netPay',
      title: 'Net Pay',
      dataIndex: 'net',
      align: 'right',
      sorter: true,
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Other Payment',
      title: 'Other Payment',
      dataIndex: '_otherPayment',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Other Deduction',
      title: 'Other Deduction',
      dataIndex: '_otherDeduction',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(
            Math.abs(value || 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'ssp',
      title: 'Sick Pay (SSP)',
      dataIndex: 'payeeUmbrellaValue',
      align: 'right',
      sorter: true,
      width: 250,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value?.ssp || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'Payment Date',
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      align: 'right',
      width: 250,
      render: (value) => (
        <div className="row_center">
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </div>
      ),
    },
    {
      key: 'department',
      title: 'Department',
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => (
        <div className="row_center">{value?.department || '_'}</div>
      ),
    },
    {
      key: 'Payslip ID',
      title: 'Payslip ID',
      dataIndex: 'id',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {(value || '')?.replace('SFB', 'PAY') || '_'}
        </div>
      ),
    },
    {
      key: 'holidayPay',
      title: 'Advanced Holiday Pay',
      dataIndex: 'rolledUpHolidayPay',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'holidayPayRetained',
      title: 'Holiday Pay Retained',
      dataIndex: 'retainedHolidayPay',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
    {
      key: 'holidayPaid',
      title: 'Holiday Paid',
      align: 'right',
      width: 200,
      render: (_, record) => (
        <div className="row_center">
          {formatMoney(
            (record.holidayPaid || 0) - (record.rolledUpHolidayPay || 0),
            showCurrencyByCountry(payrollCountry),
          )}
        </div>
      ),
    },
    {
      key: 'RCT Status',
      title: 'RCT Status',
      dataIndex: 'cisStatus',
      align: 'center',
      width: 200,
      render: (value) => <div className="row_center">{value || '_'}</div>,
    },
    {
      key: 'RCT Deduction',
      title: 'RCT Deduction',
      dataIndex: 'cisDeduction',
      align: 'right',
      width: 200,
      render: (value) => (
        <div className="row_center">
          {formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}
        </div>
      ),
    },
  ];

  const handleSelectGroup = (
    group: GetGroupingPayrollByPeriodResponse['data'][0],
  ) => {
    setIsGrouping(false);
    handleFetchData({
      page: 0,
      size: 20,
      payFrequency: group?.payFrequency,
      taxPeriod: group?.taxPeriod,
    });
  };

  const { run: sendEmailPayslipMultiple } = useSendEmailPayslipMultiple();
  const handleSendEmailPayslipMultiple = (data: Payroll[]) => {
    sendEmailPayslipMultiple(data.map((item) => item.id)).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentPayslipPending'],
        });
      }
    });
  };
  const handleSendEmailPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendEmailPayslipMultiple(selectedRows);
  };
  useNotificationSocket({
    eventName: `send-mail-self-bill-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.emailSentPayslipSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.emailSentPayslipFailed'],
      });
    },
  });

  const handleExport = (data: Payroll[]) => {
    const dataExport = data.map((item) => {
      const {
        companyRef,
        subcontractorRef,
        payeeUmbrellaValue,
        agencyName,
        taxPeriod,
        taxYear,
        payFrequency,
        timesheetValue,
        _otherPayment,
        _otherDeduction,
        payments,
        totalPayment,
        totalDeduction,
        rolledUpHolidayPay,
        retainedHolidayPay,
        holidayPaid,
        paymentDate,
        net,
        salary,
        expenseClaimed,
        paid,
        timeSheetRef,
        invoiceNo,
        invoiceNos,
        outstanding,
        studentLoan,
        postgraduateLoan,
        selfBillRef,
        displayedTaxablePay,
      } = item;
      return {
        companyID: companyRef?.id || '',
        subcontractorID: subcontractorRef?.id || '',
        agencyRef: `${subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
          subcontractorRef?.agencyRef || ''
        }`,
        foreNames: subcontractorRef?.forename || '',
        surname: subcontractorRef?.surname || '',
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || selfBillRef?.taxYear || '',
        paymentModel:
          paymentModels[
            subcontractorRef?.paymentModel as keyof typeof paymentModels
          ] || '',
        payFrequency: payFrequency
          ? find(
              defaultPaymentFrequency,
              (item) => item?.value === payFrequency,
            )?.label
          : '',
        paymentCompany: companyRef?.companyName || '',
        agencyName: agencyName || '',
        timesheetValue: formatMoneyExport(
          Number(timesheetValue),
          showCurrencyByCountry(payrollCountry),
        ),
        troncValue: formatMoneyExport(
          Number(item?.payeeUmbrellaValue?.troncValue),
          showCurrencyByCountry(payrollCountry),
        ),
        hours: payeeUmbrellaValue?.totalHours || 0,
        margin: formatMoneyExport(
          Number(payeeUmbrellaValue?.payslipMargin),
          showCurrencyByCountry(payrollCountry),
        ),
        apprenticeLevy: formatMoneyExport(
          Number(payeeUmbrellaValue?.appLevy),
          showCurrencyByCountry(payrollCountry),
        ),
        salary: formatMoneyExport(
          Number(salary),
          showCurrencyByCountry(payrollCountry),
        ),
        rolledUpHolidayPay: formatMoneyExport(
          Number(rolledUpHolidayPay),
          showCurrencyByCountry(payrollCountry),
        ),
        expenseClaimed: formatMoneyExport(
          Number(expenseClaimed),
          showCurrencyByCountry(payrollCountry),
        ),
        expensesPaid: formatMoneyExport(
          Number(payeeUmbrellaValue?.expensesPaid),
          showCurrencyByCountry(payrollCountry),
        ),
        bonus:
          subcontractorRef?.paymentModel !== 'PAYEE'
            ? formatMoneyExport(
                Number(payeeUmbrellaValue?.bonus),
                showCurrencyByCountry(payrollCountry),
              )
            : '',
        taxablePay: formatMoneyExport(
          Number(displayedTaxablePay),
          showCurrencyByCountry(payrollCountry),
        ),
        grossForTax: formatMoneyExport(
          Number(payeeUmbrellaValue?.grossForTax),
          showCurrencyByCountry(payrollCountry),
        ),
        grossForNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.grossForNI),
          showCurrencyByCountry(payrollCountry),
        ),
        otherPayment: formatMoneyExport(
          Number(_otherPayment),
          showCurrencyByCountry(payrollCountry),
        ),
        otherDeduction: formatMoneyExport(
          Math.abs(Number(_otherDeduction) || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        description: !isEmpty(payments) ? payments[0].description || '' : '',
        totalPayment: formatMoneyExport(totalPayment),
        taxDeducted: formatMoneyExport(
          Number(payeeUmbrellaValue?.tax),
          showCurrencyByCountry(payrollCountry),
        ),
        employeeNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.employeeNI),
          showCurrencyByCountry(payrollCountry),
        ),
        employersNI: formatMoneyExport(
          Number(payeeUmbrellaValue?.employersNI),
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          getShownEmployeePension(item),
          showCurrencyByCountry(payrollCountry),
        ),
        eePensionDeductionMethod: payeeUmbrellaValue?.eePensionDeductionMethod
          ? pensionDeductionMethods.find(
              (item) =>
                item.value === payeeUmbrellaValue?.eePensionDeductionMethod,
            )?.label
          : 'Percentage (%)',
        employersPension: formatMoneyExport(
          getShownEmployersPension(item),
          showCurrencyByCountry(payrollCountry),
        ),
        erPensionDeductionMethod: payeeUmbrellaValue?.erPensionDeductionMethod
          ? pensionDeductionMethods.find(
              (item) =>
                item.value === payeeUmbrellaValue?.erPensionDeductionMethod,
            )?.label
          : 'Percentage (%)',
        holidayPayRetained: formatMoneyExport(
          retainedHolidayPay,
          showCurrencyByCountry(payrollCountry),
        ),
        totalDeductions: formatMoneyExport(
          totalDeduction,
          showCurrencyByCountry(payrollCountry),
        ),
        netPay: formatMoneyExport(
          Number(net),
          showCurrencyByCountry(payrollCountry),
        ),
        studentLoan: formatMoneyExport(
          Number(studentLoan),
          showCurrencyByCountry(payrollCountry),
        ),
        postgraduateLoan: formatMoneyExport(
          Number(postgraduateLoan),
          showCurrencyByCountry(payrollCountry),
        ),
        attachment: formatMoneyExport(
          payeeUmbrellaValue?.attachmentOrderDeducted,
          showCurrencyByCountry(payrollCountry),
        ),
        slickPay: formatMoneyExport(
          Number(payeeUmbrellaValue?.ssp),
          showCurrencyByCountry(payrollCountry),
        ),
        smp: formatMoneyExport(
          Number(payeeUmbrellaValue?.smp),
          showCurrencyByCountry(payrollCountry),
        ),
        sncp: formatMoneyExport(
          Number(payeeUmbrellaValue?.sncp),
          showCurrencyByCountry(payrollCountry),
        ),
        adoptionPay: formatMoneyExport(
          Number(payeeUmbrellaValue?.sap),
          showCurrencyByCountry(payrollCountry),
        ),
        paternityPay: formatMoneyExport(
          payeeUmbrellaValue?.spp || payeeUmbrellaValue?.sppa,
          showCurrencyByCountry(payrollCountry),
        ),
        additionalSPP: formatMoneyExport(
          0,
          showCurrencyByCountry(payrollCountry),
        ),
        grossForTaxYTD: formatMoneyExport(
          (item?.payeeUmbrellaValue?.grossForTaxTD || 0) +
            (item?.payeeUmbrellaValue?.p45GrossToDate || 0) +
            (item?.payeeUmbrellaValue?.p11?.grossForTax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        taxYTD: formatMoneyExport(
          (item?.payeeUmbrellaValue?.taxTD || 0) +
            (item?.payeeUmbrellaValue?.p45TaxToDate || 0) +
            (item?.payeeUmbrellaValue?.p11?.tax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        paymentDate: paymentDate
          ? moment(paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        selfBillID: selfBillRef?.id?.replace('SFB', 'PAY') || '',
        invoiceNo: timeSheetRef
          ? invoiceNo || ''
          : !invoiceNos?.length
            ? ''
            : invoiceNos.length === 1
              ? invoiceNos[0]
              : 'Multi',
        paid: formatMoneyExport(paid, showCurrencyByCountry(payrollCountry)),
        outstanding: formatMoneyExport(
          outstanding,
          showCurrencyByCountry(payrollCountry),
        ),
        fpsAmount: '',
        holidayPaid: formatMoneyExport(
          (holidayPaid || 0) - (rolledUpHolidayPay || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        ...(some(defaultPaymentModel, (p) => p.value === 'PAYE_FTH')
          ? {
              takeHomePercent: formatMoneyExport(
                payeeUmbrellaValue?.takeHomePayPercent || 0,
                showCurrencyByCountry(payrollCountry),
              ),
              selfEmployedIncome: formatMoneyExport(
                payeeUmbrellaValue?.selfEmployedIncome || 0,
                showCurrencyByCountry(payrollCountry),
              ),
              additionalMargin: formatMoneyExport(
                payeeUmbrellaValue?.additionalMargin || 0,
                showCurrencyByCountry(payrollCountry),
              ),
            }
          : {}),
        avcERPension: formatMoneyExport(
          payeeUmbrellaValue?.avcERPension || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });

    const headers = [
      'Company ID',
      'Employee ID',
      'Agency Ref',
      'Forenames',
      'Surname',
      'Tax Period',
      'Tax Year',
      'Payment Model',
      'Pay Frequency',
      'Payment Company',
      'Agency Name',
      'Timesheet Values',
      'Tronc payment',
      'Hours',
      'Margin',
      'Apprentice Levy',
      'Salary',
      'Advanced Holiday Pay',
      'Expenses Claimed',
      'Expenses Paid',
      'Bonus',
      'Taxable Pay',
      'Gross for Tax',
      'Gross for NI',
      'Other Payment',
      'Other Deduction',
      'Description',
      'Total Payment',
      'Tax Deducted',
      'NI Empe',
      'NI Empr',
      'Pension Empe',
      'Empe Deduction Method',
      'Pension Empr',
      'Empr Deduction Method',
      'Holiday Pay Retained',
      'Total Deductions',
      'Net Pay',
      'Student Loan',
      'PG Student Loan',
      'Attachment',
      'Sick Pay (SSP)',
      'Maternity Pay (SMP)',
      'Neonatal Care Pay (SNCP)',
      'Adoption Pay (SAP)',
      'Paternity Pay (SPP)',
      'Additional SPP',
      'Gross for Tax (YTD)',
      'Tax (YTD)',
      'Payment Date',
      'Payslip ID',
      'Sales Inv Ref',
      'Paid',
      'Outstanding',
      'FPS Amount',
      'Holiday Paid',
      ...(some(defaultPaymentModel, (p) => p.value === 'PAYE_FTH')
        ? ['Take Home %', 'Umbrella Adv/Self Employed Pmt', 'Additional Margin']
        : []),
      'Employer AVC',
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `Payroll-export-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}` || 'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const { run: getPayroll } = useGetPayroll();
  const handleExportAll = () => {
    getPayroll({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      paymentModels: validPaymentModels,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">Email Payslip</div>
          {!isGrouping && (
            <Dropdown
              menu={{
                items: [
                  ...(canReverse
                    ? [
                        {
                          key: 'reverse',
                          label: messages['timesheet.reverse'],
                          onClick: handleOpenReverseSelected,
                        },
                      ]
                    : []),
                  {
                    key: 'sendEmailPayslip',
                    label: messages['common.sendEmailPayslip'],
                    onClick: handleSendEmailPayslipSelected,
                  },
                  {
                    key: 'export',
                    label: messages['common.export'],
                    onClick: handleExportSelected,
                  },
                  {
                    key: 'exportAll',
                    label: messages['common.exportAll'],
                    onClick: handleExportAll,
                  },
                ],
              }}
            >
              <AppButton name="Actions" rightIcon={<DownOutlined />} />
            </Dropdown>
          )}
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      titleFullWidth
    >
      {isGrouping ? (
        <AppCard loading={isPayrollGroupingLoading} className="!border-none">
          <div className="grid grid-cols-2 gap-[16px]">
            {isEmpty(payrollGroupingData) ? (
              <div className="col-span-2">
                <AppEmpty title="No payroll data found" />
              </div>
            ) : (
              payrollGroupingData?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border border-neutral-100 rounded-[8px] p-[16px] cursor-pointer"
                  onClick={() => handleSelectGroup(item)}
                >
                  <div>
                    <div>
                      {defaultPaymentFrequency.find(
                        (i) => i.value === item.payFrequency,
                      )?.label ?? ''}
                    </div>
                    <div>{`Period ${item.taxPeriod}`}</div>
                  </div>
                  <Tag>{`${item.countCompany} payroll`}</Tag>
                </div>
              ))
            )}
          </div>
        </AppCard>
      ) : (
        <AppsContent>
          <div className="flex items-center justify-between gap-x-[16px] mb-[20px]">
            <AppBackButton onClick={() => setIsGrouping(true)} />
            <AppInputSearch onChange={handleSearch} />
          </div>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: isUK ? 9300 : 8600 }}
            columns={isUK ? columnsUK : columnsIreland}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
          />
        </AppsContent>
      )}
      <TimesheetReverseModal
        isOpen={reverseModal.isOpen}
        toggle={toggleReverseModal}
        payrollIds={reverseModal.payrollIds}
        onPerform={handleReverse}
      />
    </AppModalContainer>
  );
};

export default EmailPayslipModal;
